import { StoreInfo } from '../../types';
import { getFormattedStoreDisplayName, sanitizeStoreCoordinates } from '../../utils/storeUtils';
import { haversineMiles } from './marketDiscovery';
import { useStore } from './store';
import { getStoresNear } from '../../services/supabaseService';
import { getNewSupabaseClient } from '../../services/supabaseClient';
import { streetKey } from './receiptStore';

// The stores around the shopper, nearest first, for store detection
// (useMerchant.detectStore) and the store picker list:
//   - OpenStreetMap (Photon, one call): the nearest stores of every kind in
//     Google's Place Types Table A (STORE_TYPE_TAGS).
//   - Our store directory: stores shoppers have archived trips at. Fills what
//     open data lacks, and a store it knows keeps our record (its trips and
//     prices attach to it).
// Both run at once and search within the user's Intelligence Calibration
// radius. The result is merged, each place once, sorted by actual distance.

export const MAX_NEARBY_STORES = 40;
const DEFAULT_RADIUS_MILES = 15;
const KM_PER_MILE = 1.609344;
const PHOTON_URL = 'https://photon.komoot.io/reverse';
const PHOTON_TIMEOUT_MS = 8000;
// Two entries this close with a similar name are the same store.
const SAME_STORE_MILES = 0.1;
// A gas station's pumps this close to a shop are that station's shop.
const FUEL_SHOP_MILES = 0.03;

export type Origin = { lat: number; lng: number };

// The store types of Google's Place Types Table A
// (https://developers.google.com/maps/documentation/places/web-service/place-types#table-a:
// every Shopping type but malls, plus gas stations and pharmacies), as the
// OpenStreetMap tags those stores carry. Photon returns only places with one
// of these tags.
const STORE_TYPE_TAGS: Record<string, string[]> = {
  general_store: ['shop:general', 'shop:variety_store'],
  convenience_store: ['shop:convenience'],
  gas_station: ['amenity:fuel'],
  grocery_store: ['shop:supermarket', 'shop:grocery'], // also supermarket, discount_supermarket, hypermarket, asian_grocery_store
  market: ['amenity:marketplace'], // also farmers_market, flea_market
  food_store: ['shop:food', 'shop:greengrocer', 'shop:frozen_food'],
  butcher_shop: ['shop:butcher'],
  health_food_store: ['shop:health_food'],
  farm_shop: ['shop:farm'],
  liquor_store: ['shop:alcohol', 'shop:wine', 'shop:beverages'],
  tea_store: ['shop:tea', 'shop:coffee'],
  department_store: ['shop:department_store'],
  discount_store: ['shop:discount'],
  warehouse_store: ['shop:wholesale'], // also wholesaler
  pharmacy: ['amenity:pharmacy', 'shop:chemist', 'shop:medical_supply'], // also drugstore
  cosmetics_store: ['shop:cosmetics', 'shop:perfumery'],
  electronics_store: ['shop:electronics', 'shop:computer', 'shop:appliance', 'shop:hifi'],
  cell_phone_store: ['shop:mobile_phone'],
  hardware_store: ['shop:hardware'],
  home_improvement_store: ['shop:doityourself', 'shop:paint'],
  building_materials_store: ['shop:trade'],
  garden_center: ['shop:garden_centre'],
  home_goods_store: ['shop:houseware', 'shop:interior_decoration', 'shop:household_linen', 'shop:bed', 'shop:kitchen'],
  furniture_store: ['shop:furniture'],
  clothing_store: ['shop:clothes', 'shop:fashion', 'shop:boutique'], // also womens_clothing_store
  shoe_store: ['shop:shoes'],
  sporting_goods_store: ['shop:sports', 'shop:outdoor'], // also sportswear_store
  jewelry_store: ['shop:jewelry'],
  pet_store: ['shop:pet'],
  toy_store: ['shop:toys'],
  book_store: ['shop:books', 'shop:stationery'],
  gift_shop: ['shop:gift'],
  bicycle_store: ['shop:bicycle'],
  auto_parts_store: ['shop:car_parts'],
  thrift_store: ['shop:second_hand', 'shop:charity'],
};
const PHOTON_STORE_TAGS = Object.values(STORE_TYPE_TAGS).flat();

// OpenStreetMap maps a gas station's pumps (tagged by fuel brand: "Chevron",
// "76") apart from its shop ("Fast 'N' Esy"). The pumps carry this flag so
// they fold into the shop.
type NearbyStore = StoreInfo & { fuel?: boolean };
const isFuel = (s: StoreInfo) => !!(s as NearbyStore).fuel;

/** The user's Intelligence Calibration radius (profiles.settings.intelligence). */
const searchRadiusMiles = () =>
  useStore.getState().userProfile?.settings?.intelligence?.lattice_radius_miles || DEFAULT_RADIUS_MILES;

const distanceFrom = (origin: Origin, store: StoreInfo) => {
  const c = sanitizeStoreCoordinates(store.coordinates);
  return c ? haversineMiles(origin.lat, origin.lng, c.lat, c.lng) : Infinity;
};

// Name without the ", street" suffix our directory adds, case or punctuation.
const nameKey = (name: string) => name.split(',')[0].toLowerCase().replace(/[^a-z0-9]/g, '');

const sameStreetAddress = (a: StoreInfo, b: StoreInfo) => {
  const ak = streetKey(a.address);
  const bk = streetKey(b.address);
  return !!ak && !!bk && ak.number === bk.number && ak.street === bk.street;
};

/**
 * The same place listed twice, by two sources or by one ("76" mapped three
 * times, "Save Mart" / "SaveMart" in our directory). Pins a little apart and:
 *   - the same name, or one name extending the other ("Gulf" / "Gulf Express"); or
 *   - a gas station's pumps and a shop at the same street address or right
 *     next to them ("Chevron" / "Fast 'N' Esy"). An address alone isn't
 *     enough: different shops share one, and two fuel brands are two stations.
 */
const sameStore = (a: StoreInfo, b: StoreInfo) => {
  const ac = sanitizeStoreCoordinates(a.coordinates);
  const bc = sanitizeStoreCoordinates(b.coordinates);
  if (!ac || !bc) return false;
  const miles = haversineMiles(ac.lat, ac.lng, bc.lat, bc.lng);
  if (miles > SAME_STORE_MILES) return false;
  const ak = nameKey(a.name);
  const bk = nameKey(b.name);
  if (ak && bk) {
    const [shorter, longer] = ak.length <= bk.length ? [ak, bk] : [bk, ak];
    if (shorter === longer || (shorter.length >= 4 && longer.startsWith(shorter))) return true;
  }
  if (isFuel(a) !== isFuel(b)) return sameStreetAddress(a, b) || miles <= FUEL_SHOP_MILES;
  return false;
};

async function fetchOsmStores(origin: Origin, radiusMiles: number): Promise<StoreInfo[]> {
  const params = new URLSearchParams({
    lat: String(origin.lat),
    lon: String(origin.lng),
    radius: String(radiusMiles * KM_PER_MILE),
    limit: '50',
  });
  PHOTON_STORE_TAGS.forEach(tag => params.append('osm_tag', tag));
  // Photon answers nearest first, so a wide radius still returns the closest.
  const res = await fetch(`${PHOTON_URL}?${params}`, { signal: AbortSignal.timeout(PHOTON_TIMEOUT_MS) });
  if (!res.ok) throw new Error(`Photon ${res.status}`);
  const data = await res.json();
  return (data?.features || [])
    .filter((f: any) => f?.properties?.name)
    .map((f: any): NearbyStore => {
      const p = f.properties;
      const street = [p.housenumber, p.street].filter(Boolean).join(' ');
      const region = [p.state, p.postcode].filter(Boolean).join(' ');
      const address = [street, p.city || p.town || p.village || p.district, region].filter(Boolean).join(', ');
      const [lng, lat] = f.geometry?.coordinates || [];
      return {
        name: p.name,
        address: address || undefined,
        coordinates: sanitizeStoreCoordinates({ lat, lng }),
        ...(p.osm_key === 'amenity' && p.osm_value === 'fuel' ? { fuel: true } : {}),
      };
    })
    .filter((s: StoreInfo) => !!s.coordinates);
}

/**
 * The stores near `origin`, nearest first, each place once, named
 * "<store>, <street address>" like the current store and archived trips. When
 * a place is listed more than once, the entry kept is our directory's record,
 * then a shop's name, then a gas station's fuel brand. A source that fails is
 * left out.
 */
export async function findNearbyStores(origin: Origin): Promise<StoreInfo[]> {
  const radius = searchRadiusMiles();
  const settle = (source: string, request: Promise<StoreInfo[]>) =>
    request.catch((err) => {
      console.warn(`[nearbyStores] ${source} lookup failed:`, err);
      return [] as StoreInfo[];
    });
  const [osm, directory] = await Promise.all([
    settle('OpenStreetMap', fetchOsmStores(origin, radius)),
    settle('Store directory', getStoresNear(getNewSupabaseClient(), origin.lat, origin.lng, radius, MAX_NEARBY_STORES)),
  ]);

  const candidates = [...directory, ...osm.filter(s => !isFuel(s)), ...osm.filter(isFuel)];
  const merged: StoreInfo[] = [];
  candidates.forEach(s => { if (!merged.some(m => sameStore(m, s))) merged.push(s); });

  return merged
    .filter(s => distanceFrom(origin, s) <= radius)
    .sort((a, b) => distanceFrom(origin, a) - distanceFrom(origin, b))
    .slice(0, MAX_NEARBY_STORES)
    .map(({ fuel: _fuel, distanceMiles: _d, ...s }: any): StoreInfo => ({ ...s, name: getFormattedStoreDisplayName(s) }));
}
